import React from 'react';

import { OrcamentoCilia, Requisicao } from '../../../interfaces/OrcamentoCilia';

/**
 * O "Motivo da Requisição" no cartão do topo do detalhe (orcamento-cilia/at-18): o texto que o
 * comprador lê no campo de mesmo nome do Nasajon, visível ao entrar.
 *
 * Três estados, que não podem se parecer:
 * - **em conferência** (ou reaberta para ajuste): a PRÉVIA, montada pelo servidor com a mesma
 *   função do envio;
 * - **enviada**: o texto GRAVADO no envio, requisição a requisição (normal e CAMINHO VIP);
 * - **enviada antes de o sistema gravar o motivo**: "não registrado". Nunca recalculado: o
 *   recalculado pode não ser o que o comprador leu.
 */
interface Props {
  orcamento: Pick<OrcamentoCilia, 'estado' | 'motivoPrevisto'>;
  requisicoes: Requisicao[];
}

const ESTADOS_DE_CONFERENCIA = ['EM_CONFERENCIA', 'EM_AJUSTE'];

const Texto: React.FC<{ rotulo: string; texto: string | null }> = ({ rotulo, texto }) => (
  <div className="grid-item">
    <span className="label">{rotulo}</span>
    {texto ? (
      <span className="value" style={{ whiteSpace: 'pre-wrap', fontWeight: 400, fontSize: 14, tabSize: 2 }}>
        {texto}
      </span>
    ) : (
      <span className="value" style={{ fontWeight: 400, fontSize: 14, color: 'var(--tema-texto-3, #64748b)' }}>
        Motivo não registrado: esta requisição foi enviada antes de o sistema passar a guardar o
        motivo. O texto enviado está na requisição, no Nasajon.
      </span>
    )}
  </div>
);

const MotivoDaRequisicao: React.FC<Props> = ({ orcamento, requisicoes }) => {
  const emConferencia = ESTADOS_DE_CONFERENCIA.includes(orcamento.estado);

  let blocos: Array<{ rotulo: string; texto: string | null }> = [];
  if (emConferencia) {
    // Backend anterior à at-18 não manda a prévia: melhor não mostrar nada que mostrar vazio.
    if (!orcamento.motivoPrevisto) return null;
    blocos.push({ rotulo: 'Motivo da Requisição que será enviado', texto: orcamento.motivoPrevisto.normal });
    if (orcamento.motivoPrevisto.vip) {
      blocos.push({ rotulo: 'Motivo da requisição CAMINHO VIP que será enviado', texto: orcamento.motivoPrevisto.vip });
    }
  } else {
    // As requisições da versão mais recente do envio (a normal primeiro; a VIP é a exceção).
    const ultimaVersao = Math.max(0, ...requisicoes.map((r) => r.versao));
    const atuais = requisicoes
      .filter((r) => r.versao === ultimaVersao)
      .sort((a, b) => Number(a.vip) - Number(b.vip));
    // Concluída sem requisição: não houve motivo enviado, e o resto da tela já diz isso.
    if (atuais.length === 0) return null;
    blocos = atuais.map((r) => ({
      rotulo: r.vip ? 'Motivo da requisição CAMINHO VIP' : 'Motivo da Requisição',
      texto: r.motivoRequisicao ?? null,
    }));
  }

  return (
    <div
      className="header-grid"
      style={{ gridTemplateColumns: blocos.length > 1 ? 'repeat(auto-fit, minmax(280px, 1fr))' : '1fr' }}
    >
      {blocos.map((b) => (
        <Texto key={b.rotulo} rotulo={b.rotulo} texto={b.texto} />
      ))}
    </div>
  );
};

export default MotivoDaRequisicao;
